import { observer } from 'mobx-react-lite'
import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useFeatureToggleStore } from 'app/stores/StoreContext'

/** Feature-toggle name gating the whole workflows builder (nav item + all /workflows routes). */
export const WORKFLOWS_FLAG = `workflows-builder`

/** Redirects home unless the workflows feature toggle is enabled — wraps every /workflows route so a
 *  direct URL can't reach the builder when the flag is off.
 *
 *  observer() + the "toggles loaded" guard matter here: on a hard refresh straight to a /workflows URL
 *  this can render before the toggle store resolves. Without the guard it would read the flag as `false`
 *  and redirect home before the real value arrives (and, not being an observer, never recover).
 *  `enabledByName` is empty until `load()` resolves, so we hold (render nothing) until then. */
export const WorkflowsGate = observer(function WorkflowsGate({ children }: { children: ReactNode }) {
  const store = useFeatureToggleStore()
  const enabled = useFeatureToggle(WORKFLOWS_FLAG)
  if (store.enabledByName.size === 0) return null // toggles not loaded yet — don't decide
  if (!enabled) return <Navigate to="/" replace />
  return <>{children}</>
})
